import moment from 'moment';

import GoalSchema from 'Shared/utils/normalizeJSON/schemas/Goal.schema';
import normalizer from 'Shared/utils/normalizeJSON/normalizer';

import WpToastManager from 'Wpds/components/toast/WpToastManager';

import { handleRequestError } from 'Shared/utils/handle-request-error';
import { showNotificationError } from 'Shared/helpers';
import { getActionItems } from 'Shared/selectors/meetingItems';
import { ItemPosition } from 'Shared/constants/types';

import GoalsConstants from '../constants/Goals.constants';
import * as GoalsAPI from '../api/Goals.api';
import { getUuidsToPositions } from './utils';

export const fetchGoalsSuccess = payload => ({
  type: GoalsConstants.Actions.FETCH_GOALS_SUCCESS,
  payload,
});

export const fetchSingleGoalSuccess = payload => ({
  type: GoalsConstants.Actions.FETCH_SINGLE_GOAL_SUCCESS,
  payload,
});

export const createGoalSuccess = payload => ({
  type: GoalsConstants.Actions.CREATE_GOAL_SUCCESS,
  payload,
});

export const updateGoalSuccess = payload => ({
  type: GoalsConstants.Actions.UPDATE_GOAL_SUCCESS,
  payload,
});

export const deleteGoalSuccess = payload => ({
  type: GoalsConstants.Actions.DELETE_GOAL_SUCCESS,
  payload,
});

export const moveGoalSuccess = payload => ({
  type: GoalsConstants.Actions.MOVE_GOAL_SUCCESS,
  payload,
});

export const markGoalCompleteSuccess = payload => ({
  type: GoalsConstants.Actions.MARK_GOAL_COMPLETE_SUCCESS,
  payload,
});

export const markGoalCompleteAndActionItemsCompleteSuccess = payload => ({
  type: GoalsConstants.Actions.MARK_GOAL_COMPLETE_AND_ACTION_ITEMS_COMPLETE_SUCCESS,
  payload,
});

export const markGoalIncompleteSuccess = payload => ({
  type: GoalsConstants.Actions.MARK_GOAL_INCOMPLETE_SUCCESS,
  payload,
});

export const changeStatusSuccess = payload => ({
  type: GoalsConstants.Actions.CHANGE_STATUS_SUCCESS,
  payload,
});

export const updateActionItemOrderPrerequest = payload => ({
  type: GoalsConstants.Actions.UPDATE_ACTION_ITEM_ORDER_PREREQUEST,
  payload,
});

export const fetchGoals = params => dispatch => {
  return GoalsAPI.fetchGoals(params)
    .then(response => {
      dispatch(fetchGoalsSuccess(normalizer(GoalSchema, response.data)));
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export const fetchSingleGoal = slug => dispatch => {
  return GoalsAPI.fetchSingleGoal(slug).then(response => {
    dispatch(fetchSingleGoalSuccess(normalizer(GoalSchema, response.data)));
    return response;
  });
};

export const createGoal = data => dispatch => {
  return GoalsAPI.createGoal(data)
    .then(response => {
      WpToastManager.success('Goal created');
      dispatch(createGoalSuccess(normalizer(GoalSchema, response.data)));
      return response.data;
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export const createGoalV2 = data => dispatch => {
  return GoalsAPI.createGoalV2(data)
    .then(response => {
      WpToastManager.success('Goal created');
      dispatch(createGoalSuccess(normalizer(GoalSchema, response.data)));
      return response.data;
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export function moveGoalWithoutRequest(goals) {
  return dispatch =>
    dispatch({
      type: GoalsConstants.Actions.MOVE_GOAL_WITHOUT_REQUEST_SUCCESS,
      payload: goals,
    });
}

export const updateGoal = (data, showToast = true) => dispatch => {
  return GoalsAPI.updateGoal(data)
    .then(response => {
      showToast && WpToastManager.success('Goal Updated');
      dispatch(updateGoalSuccess(normalizer(GoalSchema, response.data)));
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export const updateParentGoal = data => dispatch => {
  return GoalsAPI.updateParentGoal(data)
    .then(response => {
      WpToastManager.success('Goal Updated');
      dispatch(updateGoalSuccess(normalizer(GoalSchema, response.data)));
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export const deleteGoal = slug => dispatch => {
  return GoalsAPI.deleteGoal(slug)
    .then(() => {
      WpToastManager.success('Goal deleted');
      dispatch(deleteGoalSuccess(slug));
    })
    .catch(error => {
      showNotificationError(error);
    });
};

export function moveGoal(goals) {
  return dispatch => {
    return handleRequestError(GoalsAPI.moveGoal(goals.map(({ slug, position }) => ({ slug, position }))))
      .then(response => {
        dispatch(moveGoalSuccess(normalizer(GoalSchema, response.data)));
      })
      .catch(error => showNotificationError(error));
  };
}

export function markGoalComplete({ closeActionItems, goalSlug, resolutionNote }) {
  return (dispatch, getState) => {
    const state = getState();
    const currentUserId = state.user.id;
    const data = {
      completed_at: moment(),
      close_action_items: closeActionItems,
      resolution_note: resolutionNote,
      completed_by_id: currentUserId,
    };
    return GoalsAPI.changeGoalCompleteness(goalSlug, data)
      .then(response => {
        WpToastManager.success('Goal Completed!');
        dispatch(markGoalCompleteSuccess(normalizer(GoalSchema, response.data)));
        if (closeActionItems) dispatch(markGoalCompleteAndActionItemsCompleteSuccess({ goalSlug }));
        return response;
      })
      .catch(error => {
        showNotificationError(error);
      });
  };
}

export function markGoalIncomplete(slug) {
  return dispatch => {
    const data = {
      completed_at: null,
      completed_by_id: null,
    };

    return GoalsAPI.changeGoalCompleteness(slug, data)
      .then(response => {
        WpToastManager.success('Goal marked incomplete');
        dispatch(markGoalIncompleteSuccess(normalizer(GoalSchema, response.data)));
        return response;
      })
      .catch(error => {
        showNotificationError(error);
      });
  };
}

export function changeStatus(slug, status) {
  return dispatch => {
    return GoalsAPI.changeStatus(slug, status)
      .then(response => {
        WpToastManager.success('Status Updated!');
        dispatch(changeStatusSuccess(normalizer(GoalSchema, response.data)));
      })
      .catch(error => {
        showNotificationError(error);
      });
  };
}

export function saveActionItemOrder({ draggedItemUuids, hoveredItemUuid, hoverOffset, goalSlug }) {
  return (dispatch, getState) => {
    const positionField = ItemPosition.positionForGoalDetailPage;
    dispatch(updateActionItemOrderPrerequest({ draggedItemUuids, hoveredItemUuid, hoverOffset, positionField }));

    const state = getState();
    const data = {
      uuidsToPositions: JSON.stringify(
        getUuidsToPositions(getActionItems(state).filter(ai => ai.goalSlug === goalSlug))
      ),
    };

    return GoalsAPI.saveActionItemOrder(goalSlug, data).catch(error => {
      showNotificationError(error);
    });
  };
}
